class ReadMore extends HTMLElement {
  static throttle = (fn, wait) => {
    let prev, next
    return function invokeFn(...args) {
      const now = Date.now()
      next = clearTimeout(next)
      if (!prev || now - prev >= wait) {
        // eslint-disable-next-line prefer-spread
        fn.apply(null, args)
        prev = now
      } else {
        next = setTimeout(invokeFn.bind(null, ...args), wait - (now - prev))
      }
    }
  }

  constructor() {
    super()

    this.label = {
      more: 'Read more',
      less: 'Read less'
    }
    this.expanded = false
    this.originalContent = this.innerHTML.trim()
    this.indent = Number(this.dataset.indent) || 2
    this.inline = JSON.parse(this.dataset.inline || false)
    this.useMarker = JSON.parse(this.dataset.useMarker || false)
    this.marker = '[more]'
    this.handleResize = ReadMore.throttle(this.handleResize.bind(this), 500)
  }

  connectedCallback() {
    this.render()
    window.addEventListener('resize', this.handleResize)
  }

  handleResize() {
    if (this.expanded) return
    this.render()
  }

  render() {
    this.innerHTML = this.originalContent

    if (this.useMarker && this.originalContent.includes(this.marker)) {
      this.renderWithMarker()
    } else {
      const position = Number(getComputedStyle(this).webkitLineClamp)

      if (this.scrollHeight > this.offsetHeight) {
        if (!this.button) this.createButton()
        this.inline ? this.renderInline(position) : this.insertAdjacentElement('afterend', this.button)
      } else {
        this.button?.remove()
        this.button = null
      }
    }
  }

  renderWithMarker() {
    const [before, after] = this.originalContent.split(this.marker)

    this.innerHTML = `${before}...`

    if (!this.button) this.createButton()
    this.inline ? this.appendChild(this.button) : this.insertAdjacentElement('afterend', this.button)

    this.content = {
      before,
      after
    }
  }

  createButton() {
    this.button = document.createElement('button')
    this.button.className = 'button-read-more text-link'
    this.button.textContent = this.label.more
    this.button.addEventListener('click', () => this.toggleContent())
  }

  renderInline(position) {
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, null, false)
    const textNodes = []

    while (walker.nextNode()) textNodes.push(walker.currentNode)
    if (!textNodes.length) return

    const range = document.createRange()
    let lastTop = null
    let lineCount = 1
    const wordPositions = []

    textNodes.forEach((node) => {
      let startOffset = 0
      const words = node.textContent.trim().split(/\s+/)

      words.forEach((word) => {
        range.setStart(node, startOffset)
        range.setEnd(node, startOffset + word.length)

        const rect = range.getBoundingClientRect()

        if (lastTop !== null && rect.top > lastTop) lineCount++
        wordPositions.push({ index: wordPositions.length, node, startOffset, word, line: lineCount })

        lastTop = rect.top
        startOffset += word.length + 1
      })
    })

    const targetWord = wordPositions.find((word) => word.line === position)

    if (!targetWord || targetWord.index + this.indent >= wordPositions.length) return

    const targetIndex = targetWord.index + this.indent
    const allWords = this.originalContent.trim().split(/\s+/)

    this.innerHTML = `${allWords.slice(0, targetIndex).join(' ')}... `
    this.appendChild(this.button)
  }

  toggleContent() {
    this.expanded = !this.expanded
    this.button.textContent = this.expanded ? this.label.less : this.label.more

    if (this.expanded) {
      this.style.display = 'block'

      if (this.useMarker && this.content) {
        this.innerHTML = this.content.before + this.content.after
      } else {
        this.innerHTML = this.originalContent
      }

      if (!this.inline) return
      this.appendChild(this.button)
      this.button.style.display = 'block'
    } else {
      this.button.removeAttribute('style')
      this.removeAttribute('style')
      this.render()
    }
  }
}

if (!customElements.get('read-more')) {
  customElements.define('read-more', ReadMore)
}
